import isArchived from 'archive/isArchived';
import {GroupActionFunctionParameters} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableIndicatorStatus} from 'notebook/tables/TableConstants';
import {AnyStructuredTable} from 'notebook/util/NoteUtilConstants';

export enum GroupActionDisabledReason {
  LOCKED = 'LOCKED',
  NOT_IN_EXECUTABLE_CANVAS = 'NOT_IN_EXECUTABLE_CANVAS',
  VALIDATION_ERRORS = 'VALIDATION_ERRORS',
  NO_UNSUBMITTED_OR_EDITED_ROWS = 'NO_UNSUBMITTED_OR_EDITED_ROWS',
  ARCHIVED_SCHEMA = 'ARCHIVED_SCHEMA',
}

function containedTablesHaveValidationErrors(evaluatedTableMap: {
  [tableName: string]: Array<Array<EvaluatedCell>>;
}): boolean {
  return Object.values(evaluatedTableMap).some((computedTable) => hasBlockingValidationErrors(computedTable));
}

function containedTablesHaveUnsubmittedChanges(
  containedStructuredTables: Array<AnyStructuredTable>,
  evaluatedTableMap: {[tableName: string]: Array<Array<EvaluatedCell>>}
): boolean {
  const tablePairings = containedStructuredTables.map((structuredTable) => ({
    structuredTable,
    computedTable: evaluatedTableMap[structuredTable.name],
  }));
  return tablePairings.some(({structuredTable, computedTable}) =>
    tableHasUnsubmittedChanges(structuredTable, computedTable)
  );
}

/**
 * Returns the specific reason the group action button is disabled, or null if
 * it is not disabled.  Callers can use this to decide whether to override a
 * specific disable reason (e.g. allowing resubmission when a row-count
 * mismatch is detected despite row-level flags showing "all submitted").
 */
export function getGroupActionButtonDisabledReason<TGroupMetadata, TCreateGroupExtras>({
  entryOrTemplateId,
  evaluatedTableMap,
  noteLinkedGroup,
  noteLinkedGroupAdapter,
  locked,
}: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>): GroupActionDisabledReason | null {
  const containedStructuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  if (locked) {
    return GroupActionDisabledReason.LOCKED;
  } else if (!isInExecutableCanvas({entryOrTemplateId, structuredTable: containedStructuredTables[0]})) {
    return GroupActionDisabledReason.NOT_IN_EXECUTABLE_CANVAS;
  } else if (containedTablesHaveValidationErrors(evaluatedTableMap)) {
    return GroupActionDisabledReason.VALIDATION_ERRORS;
  } else if (!containedTablesHaveUnsubmittedChanges(containedStructuredTables, evaluatedTableMap)) {
    return GroupActionDisabledReason.NO_UNSUBMITTED_OR_EDITED_ROWS;
  } else if (noteLinkedGroupAdapter.getGroupSchemas(noteLinkedGroup).some((schema) => isArchived(schema))) {
    return GroupActionDisabledReason.ARCHIVED_SCHEMA;
  }
  return null;
}

export default function isGroupActionButtonDisabled<TGroupMetadata, TCreateGroupExtras>(
  params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): boolean {
  return getGroupActionButtonDisabledReason(params) != null;
}

export function getGroupActionButtonTableStatus<TGroupMetadata, TCreateGroupExtras>({
  evaluatedTableMap,
  noteLinkedGroup,
  noteLinkedGroupAdapter,
}: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>): TableIndicatorStatus {
  const containedStructuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  if (containedTablesHaveValidationErrors(evaluatedTableMap)) {
    return TableIndicatorStatus.ERROR;
  } else if (containedTablesHaveUnsubmittedChanges(containedStructuredTables, evaluatedTableMap)) {
    return TableIndicatorStatus.UNSUBMITTED_CHANGES;
  } else if (noteLinkedGroupAdapter.getGroupSchemas(noteLinkedGroup).some((schema) => isArchived(schema))) {
    return TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED;
  }
  return TableIndicatorStatus.NO_STATUS;
}

const DISABLED_REASON_TOOLTIPS: Record<GroupActionDisabledReason, string> = {
  [GroupActionDisabledReason.LOCKED]: 'Entry is locked',
  [GroupActionDisabledReason.NOT_IN_EXECUTABLE_CANVAS]:
    'Submission not allowed in templates or config documents',
  [GroupActionDisabledReason.VALIDATION_ERRORS]: 'Validation error(s) are present',
  [GroupActionDisabledReason.NO_UNSUBMITTED_OR_EDITED_ROWS]: 'All changes have been submitted',
  [GroupActionDisabledReason.ARCHIVED_SCHEMA]: 'Submission not allowed for archived schema',
};

export function getGroupActionButtonDisabledTooltip<TGroupMetadata, TCreateGroupExtras>(
  params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): string | null {
  const reason = getGroupActionButtonDisabledReason(params);
  if (reason == null) {
    return null;
  }
  return DISABLED_REASON_TOOLTIPS[reason];
}
